import {KeyValuePipe} from "@angular/common"
import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, model, output, signal} from "@angular/core"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {OperatorListItemComponent} from "@app/textures/texture-editor/operators-panel/operator-list-item/operator-list-item.component"
import {createBrowserLogger} from "@cm/browser-log"
import {ListItemComponent} from "@common/components/item"
import {Operator, OperatorType} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {byOperatorType} from "@app/textures/texture-editor/operators-panel/operator-desc"
import {OperatorStack} from "@app/textures/texture-editor/operator-stack/operator-stack"
import {DialogComponent} from "@app/common/components/dialogs/dialog/dialog.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog} from "@angular/material/dialog"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {HintComponent} from "@common/components/hint/hint.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {bytesToSize} from "@cm/utils"
import {AtlasStats, CacheStats} from "@cm/hal"
import {SliderComponent} from "@common/components/inputs/slider/slider.component"
import {MAX_MIP_LEVELS} from "@cm/webgl2"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {WebGl2Settings} from "@cm/webgl2/settings"
import {dataIntegrityChecker, enableDataIntegrityChecker} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/data-integrity-checker"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {graphToJson} from "@cm/utils/graph-json"
import {TextureEditorCreateJobGQL} from "@app/textures/service/textures-api.generated"
import {JobNodes} from "@cm/job-nodes/job-nodes"
import {ImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {AuthService} from "@common/services/auth/auth.service"
import {generateThumbnailsDataObjectTask} from "@cm/job-nodes"
import {JobType} from "@generated"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-operators-panel",
    templateUrl: "./operators-panel.component.html",
    styleUrls: ["./operators-panel.component.scss"],
    imports: [
        OperatorListItemComponent,
        MatTooltipModule,
        ListItemComponent,
        MatMenuModule,
        KeyValuePipe,
        HintComponent,
        InspectorSectionComponent,
        ToggleComponent,
        SliderComponent,
        ButtonComponent,
    ],
})
export class OperatorsPanelComponent {
    readonly $disabled = input(false, {alias: "disabled"})
    readonly $operatorStack = model.required<OperatorStack>({alias: "operatorStack"})
    readonly importOperators = output<void>()

    protected readonly permission = inject(PermissionsService)
    $can = this.permission.$to
    private readonly destroyRef = inject(DestroyRef)
    private readonly dialog = inject(MatDialog)
    private readonly authService = inject(AuthService)

    ngOnInit() {
        this.$operatorStack()
            .selectedOperatorChanged.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.onSelectedOperatorChanged())
        this.$operatorStack()
            .resultImageChanged.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.$_cacheStatsTick.update((v) => v + 1))
    }

    // get maxCacheSizeGB(): number {
    //     const setCacheSize = this.$operatorStack().imageCacheWebGL2.maxCacheSize
    //     if (setCacheSize === "auto") {
    //         console.log("Cache size is set to auto which is not supported in the UI")
    //         return 64
    //     }
    //     return setCacheSize / (1024 * 1024 * 1024)
    // }
    //
    // set maxCacheSizeGB(value: number) {
    //     this.$operatorStack().imageCacheWebGL2.maxCacheSize = value * 1024 * 1024 * 1024
    // }

    readonly $isDebugEnabled = computed(() => this.$operatorStack().isDebugEnabled)

    readonly $canRegenerateSourceThumbnails = computed(() => this.authService.isSuperuser())

    private textureEditorCreateJob = inject(TextureEditorCreateJobGQL)
    protected async regenerateSourceThumbnails() {
        const callback = this.$operatorStack()
        const dataObjectId = callback.textureEditorData.textureTypeSpecific?.sourceDataObject.legacyId
        if (!dataObjectId) {
            throw new Error("No source data object id found")
        }
        const graph = JobNodes.jobGraph(
            JobNodes.task(generateThumbnailsDataObjectTask, {
                input: JobNodes.value({
                    dataObject: JobNodes.dataObjectReference(dataObjectId),
                }),
            }),
            {
                platformVersion: "TODO",
            },
        )
        const result = await mutateThrowingErrors(this.textureEditorCreateJob)({
            input: {
                jobType: JobType.DataObjectThumbnails,
                name: `Regenerating thumbnails`,
                organizationLegacyId: callback.organization.legacyId,
                graph: graphToJson(graph, createBrowserLogger("textures")),
            },
        })
        const jobId = result.createJob.id
        if (jobId === undefined) {
            throw new Error("Failed to create job")
        }
    }

    readonly $operators = computed(() => this.$operatorStack().operators)

    protected readonly $selectedOperator = computed(() => this.$operatorStack().selectedOperator)

    readonly $operatorDescByType = computed(() => byOperatorType)

    readonly $canAddAutoTiling = computed(() => !this.$disabled() && !this.$operatorStack().hasAppliedOperators())

    protected readonly $canAddOperator = computed(() => !this.$disabled() && this.$operatorStack().hasAppliedOperators())

    protected isOperatorTypeAllowed(operatorType: OperatorType): boolean {
        if (operatorType === "operator-initial") {
            return false
        }
        if (operatorType === "operator-test") {
            return this.$operatorStack().isDebugEnabled && this.$can().create.testOperator()
        }
        if (operatorType === "operator-tiling") {
            const hasTilingOperator = this.$operators().some((operator) => operator.type === "operator-tiling")
            return !hasTilingOperator // only allow adding a tiling operator if there is no other tiling operator yet
        }
        return true
    }

    protected async addOperator(operatorType: OperatorType) {
        const operatorStack = this.$operatorStack()
        operatorStack.muteUpdates = true // creation and selection causes reevaluation of the operator stack, so we mute updates to avoid it
        const operator = await operatorStack.createOperator(operatorType)
        operator.markEdited()
        this.selectOperator(operator)
        operatorStack.muteUpdates = false
    }

    protected selectOperator(operator: Operator): void {
        this.$operatorStack().selectedOperator = operator
    }

    protected lockOperator(operator: Operator): void {
        this.$operatorStack().setOperatorLocked(operator, !operator.$locked())
    }

    protected enableOperator(operator: Operator): void {
        this.$operatorStack().setOperatorEnabled(operator, !operator.$enabled())
    }

    protected async duplicateOperator(operator: Operator) {
        await this.$operatorStack().duplicateOperator(operator, true)
    }

    protected deleteOperator(operator: Operator) {
        const dialogRef = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Remove operator",
                message:
                    "The currently selected operator will be removed. " +
                    "This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?",
                confirmLabel: "Remove operator",
                cancelLabel: "Cancel",
            },
        })
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.$operatorStack().removeOperator(operator)
                }
            })
    }

    readonly $minMipLevel = computed(() => TextureEditorSettings.MinMipLevel)

    protected setMinMipLevel(value: number) {
        TextureEditorSettings.MinMipLevel = value
        this.$operatorStack().requestUpdateResultImage()
    }

    readonly $pixelSize = computed(() => TextureEditorSettings.PixelSize)

    protected setPixelSize(value: number) {
        TextureEditorSettings.PixelSize = value
        this.$operatorStack().requestUpdateResultImage()
    }

    protected listImages(allocatedOnly: boolean) {
        this.$operatorStack().halContext.listImages(allocatedOnly)
    }

    protected freeAllUnusedImages() {
        this.$operatorStack().halContext.freeAllUnusedImages()
    }

    protected requestRedraw() {
        this.$operatorStack().tilingCanvas.requestRedraw()
    }

    private readonly $_cacheStatsTick = signal(0)

    protected getContentStats(cacheStats: CacheStats): string {
        return `${cacheStats.numAvailableImages + cacheStats.numUsedImages} cached (${cacheStats.numUsedImages} used, ${cacheStats.numAvailableImages} available)`
    }

    protected getMemStats(cacheStats: CacheStats): string {
        return `${bytesToSize(cacheStats.usedSizeInBytes)}${cacheStats.maxSizeInBytes === "auto" ? "" : "/" + bytesToSize(cacheStats.maxSizeInBytes)} used`
    }

    protected getAtlasStats(stats: AtlasStats): string {
        return `Atlas allocated ${bytesToSize(stats.totalSizeInBytes)} in ${stats.numPages} pages (${stats.numUsedPages} used)`
    }

    readonly $physicalCacheStats = computed(() => {
        this.$_cacheStatsTick()
        const cacheStats = this.$operatorStack().halContext.stats.caches.physical
        if (!cacheStats) {
            return undefined
        }
        return {
            content: this.getContentStats(cacheStats),
            mem: this.getMemStats(cacheStats),
        }
    })

    readonly $virtualCacheStats = computed(() => {
        this.$_cacheStatsTick()
        const cacheStats = this.$operatorStack().halContext.stats.caches.virtual
        if (!cacheStats) {
            return undefined
        }
        return {
            content: this.getContentStats(cacheStats),
            mem: this.getMemStats(cacheStats),
            atlases: cacheStats.atlasStats.map((stats) => this.getAtlasStats(stats)),
        }
    })

    protected async exportNodeGraph() {
        // await this.$operatorStack().exportOperatorNodeGraph()
        this.$operatorStack().tilingCanvas.exportResultImageGraph()
    }

    protected readonly $enableIntegrityChecks = computed(() => dataIntegrityChecker !== null)

    protected setEnableIntegrityChecks(value: boolean) {
        enableDataIntegrityChecker(value)
    }

    readonly $selectedOperatorIndex = signal(0)

    private onSelectedOperatorChanged() {
        const selectedOperator = this.$selectedOperator()
        this.$selectedOperatorIndex.set(selectedOperator ? this.$operators().indexOf(selectedOperator) : -1)
    }

    protected forceFullRedraw() {
        ImageOpHal.freeAllPages()
        this.$operatorStack().requestUpdateResultImage()
    }

    protected readonly ImageOpNode = ImageOpNode
    protected readonly ImageOpHal = ImageOpHal
    protected readonly TextureEditorSettings = TextureEditorSettings
    protected readonly MAX_MIP_LEVELS = MAX_MIP_LEVELS
    protected readonly WebGl2Settings = WebGl2Settings
}
